<template>
  <div class="sf-page">
    <v-progress-linear color="accent"
                       indeterminate
                       v-bind:active="loading" />
    <div v-if="customEmailTemplate != null">
      <v-snackbar right
                  top
                  v-model="snackbar">
        {{ snackbarText }}
      </v-snackbar>
      <confirm-dialog ref="leaveDialog"
                      v-bind:cancel-button-label="$t('stay')"
                      v-bind:confirm-button-label="$t('leave')"
                      v-bind:message="$t('unsavedChanges')"
                      v-bind:title="$t('unsavedChangesTitle')"
                      v-model="leaveDialog" />
      <confirm-dialog ref="deleteDialog"
                      v-bind:cancel-button-label="$t('cancel')"
                      v-bind:confirm-button-label="$t('remove')"
                      v-bind:message="$t('confirmDeleteTemplate')"
                      v-bind:title="$t('remove')"
                      v-model="deleteDialog"
                      v-on:confirm="remove" />
      <top-header v-bind:title="customEmailTemplate.name | defaultValue($t('untitledCustomEmailTemplate'))">
        <v-btn class="sf-back-btn"
               exact
               slot="left-button"
               text
               v-bind:to="{ name: 'administration-custom-email-templates' }">
          <v-icon left>ti-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn color="primary"
               depressed
               exact
               slot="right-button"
               v-bind:disabled="!isDirty"
               v-bind:loading="saving"
               v-if="!customEmailTemplate.requiresConversion"
               v-on:click="save">
          <v-icon left>ti-check</v-icon>
          {{ $t('save') }}
        </v-btn>
        <v-menu offset-y
                left
                slot="menu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon
                   v-bind="attrs"
                   v-bind:title="$t('actions')"
                   v-on="on">
              <v-icon>ti-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-on:click="deleteDialog = true">
              <v-list-item-icon class="mr-3"><v-icon color="error">ti-trash</v-icon></v-list-item-icon>
              <v-list-item-title class="error--text">{{ $t('remove') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-tabs class="sf-tabs"
                slot="tabs"
                v-if="!customEmailTemplate.requiresConversion"
                v-model="tab"
                v-on:change="onTabChange">
          <v-tab>
            <v-icon left>ti-palette</v-icon>
            {{ $t('design') }}
          </v-tab>
          <v-tab>
            <v-icon left>ti-settings</v-icon>
            {{ $t('settings') }}
          </v-tab>
          <v-tab>
            <v-icon left>ti-eye</v-icon>
            {{ $t('previewAndTest') }}
          </v-tab>
        </v-tabs>
      </top-header>
      <email-test-mode-banner />
      <div class="sf-container"
           v-if="errorAlert">
        <v-alert class="sf-alert"
                 text
                 type="error"
                 v-model="errorAlert">
          <div v-bind:key="index"
               v-for="(error, index) in errors">{{ error }}</div>
        </v-alert>
      </div>

      <!-- Altformat: nur noch Umwandeln (oder Löschen in der Liste), keine Bearbeitung mehr -->
      <div class="sf-container"
           v-if="customEmailTemplate.requiresConversion">
        <v-row>
          <v-col cols="12"
                 lg="5">
            <v-card class="sf-card"
                    flat>
              <div class="sf-card__header">
                <v-icon class="mr-2"
                        color="warning">ti-alert-triangle</v-icon>
                <h2 class="sf-card__title">{{ $t('conversionTitle') }}</h2>
              </div>
              <v-card-text class="sf-card__body">
                <p>{{ $t('conversionText') }}</p>
                <p class="text-caption sf-muted mb-0">{{ $t('conversionHint') }}</p>
              </v-card-text>
              <v-card-actions>
                <v-btn color="primary"
                       depressed
                       v-bind:loading="converting"
                       v-on:click="convert">
                  <v-icon left>ti-wand</v-icon>
                  {{ $t('convert') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
          <v-col cols="12"
                 lg="7">
            <p class="text-caption sf-muted mb-2">{{ $t('conversionPreview') }}</p>
            <iframe class="email-preview email-preview--framed"
                    sandbox=""
                    title="Vorschau"
                    v-bind:srcdoc="legacyPreviewHtml"></iframe>
          </v-col>
        </v-row>
      </div>

      <template v-else>
      <!-- Design -->
      <div class="sf-container sf-container--fluid"
           v-show="tab === 0">
          <email-designer ref="designer"
                          v-bind:access-token="accessToken"
                          v-bind:api-base-url="designerSettings.apiBaseUrl"
                          v-bind:assets="emailAssets"
                          v-bind:audience="customEmailTemplate.audience"
                          v-bind:base-url="designerSettings.baseUrl"
                          v-bind:call-to-action-targets="emailDesigner.callToActionTargets"
                          v-bind:key="designerKey"
                          v-bind:placeholders="emailDesigner.placeholders"
                          v-bind:show-code="showCode"
                          v-bind:upload-url="emailAssetUploadUrl"
                          v-bind:value="{ design: customEmailTemplate.design, mjml: customEmailTemplate.mjml }"
                          v-if="designerReady"
                          v-on:dirty="designerDirty = true" />
      </div>

      <!-- Einstellungen -->
      <div class="sf-container"
           v-show="tab === 1">
        <v-row>
          <v-col cols="12"
                 lg="8">
            <custom-email-template-edit-card ref="editCard"
                                             v-bind:customEmailTemplate="customEmailTemplate"
                                             v-bind:edit-mode="true" />
          </v-col>
          <v-col cols="12"
                 lg="4">
            <v-card class="sf-card"
                    flat>
              <div class="sf-card__header">
                <h2 class="sf-card__title">{{ $t('developerOptions') }}</h2>
              </div>
              <v-card-text class="sf-card__body">
                <v-switch class="mt-0"
                          color="primary"
                          hide-details
                          inset
                          v-bind:label="$t('showCode')"
                          v-model="showCode" />
                <p class="text-caption sf-muted mt-3 mb-0">{{ $t('showCodeHint') }}</p>
                <v-divider class="my-5" />
                <h3 class="sf-section-title">{{ $t('images') }}</h3>
                <p class="text-caption sf-muted">{{ $t('importImagesHint', { count: emailAssets.length }) }}</p>
                <v-btn color="primary"
                       outlined
                       small
                       v-bind:loading="importingAssets"
                       v-on:click="importDefaultAssets">
                  <v-icon left
                          small>ti-photo-plus</v-icon>
                  {{ $t('importImages') }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Vorschau & Test -->
      <div class="sf-container"
           v-show="tab === 2">
        <v-row>
          <v-col cols="12"
                 lg="8">
            <v-card class="sf-card"
                    flat>
              <div class="sf-card__header">
                <h2 class="sf-card__title">{{ $t('preview') }}</h2>
                <div class="sf-card__tools">
                  <v-btn-toggle class="sf-toggle"
                                dense
                                mandatory
                                v-model="previewDevice">
                    <v-btn icon
                           small
                           value="desktop">
                      <v-icon small>ti-device-desktop</v-icon>
                    </v-btn>
                    <v-btn icon
                           small
                           value="mobile">
                      <v-icon small>ti-device-mobile</v-icon>
                    </v-btn>
                  </v-btn-toggle>
                  <v-btn icon
                         v-bind:loading="previewLoading"
                         v-bind:title="$t('refresh')"
                         v-on:click="refreshPreview">
                    <v-icon>ti-refresh</v-icon>
                  </v-btn>
                </div>
              </div>
              <v-card-text class="sf-card__body">
                <v-autocomplete dense clearable
                                hide-details
                                outlined
                                v-bind:items="sampleItems"
                                v-bind:label="$t('sampleRecipient')"
                                v-bind:loading="samplesLoading"
                                v-model="sampleId"
                                v-on:change="refreshPreview" />
                <v-alert class="mt-4 mb-0"
                         dense
                         text
                         type="error"
                         v-if="previewErrors.length > 0">
                  <div v-bind:key="index"
                       v-for="(error, index) in previewErrors">{{ error }}</div>
                </v-alert>
              </v-card-text>
              <div class="email-preview-frame"
                   v-bind:class="{ 'email-preview-frame--mobile': previewDevice === 'mobile' }">
                <iframe class="email-preview"
                        sandbox=""
                        title="Vorschau"
                        v-bind:srcdoc="previewHtml"></iframe>
              </div>
              <v-card-text class="sf-card__body"
                           v-if="previewText">
                <h3 class="sf-section-title">{{ $t('textVersion') }}</h3>
                <pre class="email-preview-text">{{ previewText }}</pre>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12"
                 lg="4">
            <v-card class="sf-card"
                    flat>
              <div class="sf-card__header">
                <h2 class="sf-card__title">{{ $t('testEmail') }}</h2>
              </div>
              <v-card-text class="sf-card__body">
                <p>{{ $t('testEmailText') }}</p>
                <v-text-field dense hide-details="auto"
                              outlined
                              type="email"
                              v-bind:label="$t('recipient')"
                              v-model="testEmail" />
                <p class="text-caption sf-muted mt-4 mb-0"
                   v-if="designerSettings.testMode">{{ designerSettings.guardMode }}</p>
                <p class="text-caption sf-muted mt-4 mb-0"
                   v-if="isDirty">{{ $t('testUsesSaved') }}</p>
              </v-card-text>
              <v-card-actions>
                <v-btn color="primary"
                       depressed
                       v-bind:disabled="!testEmail"
                       v-bind:loading="sendingTest"
                       v-on:click="sendTest">
                  <v-icon left>ti-mail-fast</v-icon>
                  {{ $t('sendTest') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import CustomEmailTemplateEditCard from '@/components/administration/CustomEmailTemplateEditCard'
  import EmailTestModeBanner from '@/components/administration/EmailTestModeBanner'
  import ConfirmDialog from '@/components/common/ConfirmDialog'
  import TopHeader from '@/components/common/TopHeader'

  const SHOW_CODE_KEY = 'emailDesigner.showCode'
  const TEST_EMAIL_KEY = 'emailDesigner.testEmail'

  export default {
    async beforeRouteLeave(to, from, next) {
      if (!this.isDirty) {
        next()

        return
      }

      const leave = await new Promise(resolve => this.$refs.leaveDialog.open(resolve))

      next(leave)
    },
    async beforeRouteUpdate(to, from, next) {
      await this.get(parseInt(to.params.id, 10))

      next()
    },
    components: {
      ConfirmDialog,
      CustomEmailTemplateEditCard,
      // Der Designer bringt GrapesJS + mjml-browser mit und wird nur hier geladen.
      EmailDesigner: () => import(/* webpackChunkName: "email-designer" */ '@/components/administration/EmailDesigner'),
      EmailTestModeBanner,
      TopHeader
    },
    computed: {
      ...mapGetters([ 'customers', 'emailAssets', 'emailAssetUploadUrl', 'emailDesigner', 'suppliers' ]),
      accessToken() {
        return this.$store.getters['auth/accessToken']
      },
      designerReady() {
        return this.emailDesigner.loaded && this.assetsLoaded
      },
      designerSettings() {
        return this.emailDesigner.settings || {}
      },
      isDirty() {
        return this.hasChanges || this.designerDirty
      },
      /** Bisherige Darstellung einer Altformat-Vorlage; Umgebungs-URLs für die Anzeige einsetzen. */
      legacyPreviewHtml() {
        const settings = this.designerSettings

        return (this.customEmailTemplate.code || '')
          .replace(/{{apiBaseUrl}}/g, (settings.apiBaseUrl || '').replace(/\/$/, ''))
          .replace(/{{baseUrl}}/g, (settings.baseUrl || '').replace(/\/$/, ''))
      },
      sampleItems() {
        const list = this.customEmailTemplate.audience === 'Supplier' ? this.suppliers : this.customers

        return (list || []).map(c => ({
          text: `${c.companyName || ''} ${c.familyName ? '– ' + c.familyName : ''} (${c.id})`,
          value: c.id
        }))
      }
    },
    async created() {
      this.showCode = window.localStorage.getItem(SHOW_CODE_KEY) === 'true'
      this.testEmail = window.localStorage.getItem(TEST_EMAIL_KEY) || ''

      await this.get(parseInt(this.$route.params.id, 10))
    },
    data: () => ({
      assetsLoaded: false,
      converting: false,
      customEmailTemplate: null,
      designerDirty: false,
      designerKey: 0,
      errorAlert: false,
      errors: [],
      hasChanges: false,
      id: null,
      importingAssets: false,
      deleteDialog: false,
      leaveDialog: false,
      loading: false,
      previewDevice: 'desktop',
      previewErrors: [],
      previewHtml: '',
      previewLoading: false,
      previewText: '',
      sampleId: null,
      samplesLoading: false,
      saving: false,
      sendingTest: false,
      showCode: false,
      snackbar: false,
      snackbarText: '',
      tab: 0,
      testEmail: ''
    }),
    methods: {
      async remove() {
        await this.$store.dispatch('deleteCustomEmailTemplate', { id: this.customEmailTemplate.id })

        this.hasChanges = false
        this.designerDirty = false
        this.$router.push({ name: 'administration-custom-email-templates' })
      },
      async get(id) {
        this.id = id
        this.loading = true

        await Promise.all([
          this.$store.dispatch('loadCustomEmailTemplates'),
          this.$store.dispatch('loadEmailDesignerData'),
          this.$store.dispatch('loadEmailAssets').then(() => { this.assetsLoaded = true })
        ])

        this.loading = false
        this.customEmailTemplate = Object.assign({}, this.$store.getters.customEmailTemplate(id))
        this.designerDirty = false
        this.designerKey += 1

        this.$nextTick(() => { this.hasChanges = false })
      },
      async currentDesign() {
        if (this.$refs.designer) {
          return await this.$refs.designer.getValue()
        }

        return { design: this.customEmailTemplate.design, mjml: this.customEmailTemplate.mjml }
      },
      async save() {
        this.errorAlert = false
        this.errors = []

        if (this.$refs.editCard && !this.$refs.editCard.validate()) {
          this.errors = [ this.$t('pleaseCompleteTheForm') ]
          this.errorAlert = true
          this.tab = 1

          return
        }

        const { design, mjml } = await this.currentDesign()
        const payload = { ...this.customEmailTemplate, design, mjml }

        this.saving = true

        try {
          await this.$store.dispatch('updateCustomEmailTemplate', { customEmailTemplate: payload })

          this.snackbarText = this.$t('saved')
          this.snackbar = true
          this.designerDirty = false
          this.hasChanges = false

          // Frisch vom Server laden (gerenderter Code, Modified), Designer behält seinen Zustand.
          await this.$store.dispatch('loadCustomEmailTemplates')
          const fresh = this.$store.getters.customEmailTemplate(this.id)
          this.customEmailTemplate = Object.assign({}, fresh)
          this.$nextTick(() => { this.hasChanges = false })
        } catch (e) {
          this.errors = e.errors && e.errors.length ? e.errors : [ e.message ]
          this.errorAlert = true
          this.$vuetify.goTo(0)
        } finally {
          this.saving = false
        }
      },
      async convert() {
        this.converting = true

        try {
          await this.$store.dispatch('convertCustomEmailTemplate', { id: this.id })
          await this.get(this.id)

          this.snackbarText = this.$t('converted')
          this.snackbar = true
        } catch (e) {
          this.errors = e.errors && e.errors.length ? e.errors : [ e.message ]
          this.errorAlert = true
        } finally {
          this.converting = false
        }
      },
      async onTabChange(tab) {
        if (tab === 0 && this.$refs.designer) {
          this.$nextTick(() => this.$refs.designer.refresh())
        }

        if (tab === 2) {
          await this.loadSamples()
          await this.refreshPreview()
        }
      },
      async loadSamples() {
        const action = this.customEmailTemplate.audience === 'Supplier' ? 'loadSuppliers' : 'loadCustomers'
        const list = this.customEmailTemplate.audience === 'Supplier' ? this.suppliers : this.customers

        if (list && list.length > 0) {
          return
        }

        this.samplesLoading = true

        try {
          await this.$store.dispatch(action)
        } finally {
          this.samplesLoading = false
        }
      },
      async refreshPreview() {
        this.previewErrors = []
        this.previewLoading = true

        try {
          const { mjml } = await this.currentDesign()
          const result = await this.$store.dispatch('renderCustomEmailTemplate', {
            mjml,
            audience: this.customEmailTemplate.audience,
            sampleId: this.sampleId
          })

          this.previewErrors = result.errors || []
          this.previewHtml = result.html || ''
          this.previewText = result.text || ''
        } catch (e) {
          this.previewErrors = [ e.message ]
        } finally {
          this.previewLoading = false
        }
      },
      async importDefaultAssets() {
        this.importingAssets = true

        try {
          const result = await this.$store.dispatch('importDefaultEmailAssets')
          const errors = result.errors || []

          this.snackbarText = this.$t('imagesImported', { count: result.data.length }) + (errors.length ? ` (${errors.length} ${this.$t('errors')})` : '')
          this.designerKey += 1
        } catch (e) {
          this.snackbarText = e.message
        } finally {
          this.importingAssets = false
          this.snackbar = true
        }
      },
      async sendTest() {
        this.sendingTest = true
        window.localStorage.setItem(TEST_EMAIL_KEY, this.testEmail)

        try {
          await this.$store.dispatch('sendTestCustomEmailTemplate', { id: this.id, email: this.testEmail, sampleId: this.sampleId })

          this.snackbarText = this.$t('testSent')
        } catch (e) {
          this.snackbarText = e.message
        } finally {
          this.sendingTest = false
          this.snackbar = true
        }
      }
    },
    name: 'CustomEmailTemplate',
    watch: {
      customEmailTemplate: {
        deep: true,
        handler(newValue, oldValue) {
          if (newValue == null || oldValue == null || newValue.id !== oldValue.id) {
            this.hasChanges = false
          } else {
            this.hasChanges = true
          }
        }
      },
      showCode(value) {
        window.localStorage.setItem(SHOW_CODE_KEY, value ? 'true' : 'false')
        this.designerKey += 1
      }
    }
  }
</script>

<style scoped>
  .email-preview {
    background: #ffffff;
    border: 0;
    display: block;
    height: 70vh;
    min-height: 560px;
    width: 100%;
  }

  .email-preview--framed {
    border: 1px solid var(--sf-border);
    border-radius: 16px;
  }

  .email-preview-frame {
    background: var(--sf-surface-muted);
    border-top: 1px solid var(--sf-border);
    padding: 24px;
  }

  .email-preview-frame--mobile .email-preview {
    border: 1px solid var(--sf-border);
    border-radius: 24px;
    margin: 0 auto;
    width: 375px;
  }

  .email-preview-text {
    background: var(--sf-surface-muted);
    border: 1px solid var(--sf-border);
    border-radius: 10px;
    font-size: 12px;
    max-height: 240px;
    overflow: auto;
    padding: 12px;
    white-space: pre-wrap;
  }

  .sf-toggle {
    border: 1px solid var(--sf-border-strong);
    border-radius: 10px;
  }

  .sf-toggle >>> .v-btn {
    border: 0 !important;
  }
</style>

<i18n>
  {
    "de": {
      "back": "Zurück",
      "save": "Speichern",
      "saved": "Gespeichert.",
      "design": "Design",
      "settings": "Einstellungen",
      "previewAndTest": "Vorschau & Test",
      "preview": "Vorschau",
      "textVersion": "Textversion (für Mail-Clients ohne HTML)",
      "sampleRecipient": "Beispiel-Empfänger für Platzhalter (optional)",
      "testEmail": "Testmail",
      "testEmailText": "Schickt die zuletzt gespeicherte Vorlage mit den Beispieldaten an eine Adresse.",
      "testUsesSaved": "Hinweis: Ungespeicherte Änderungen sind in der Testmail noch nicht enthalten.",
      "recipient": "Empfängeradresse",
      "sendTest": "Testmail senden",
      "testSent": "Testmail wurde übergeben.",
      "developerOptions": "Entwickleroptionen",
      "images": "Bilder",
      "importImagesHint": "Derzeit {count} Bilder im Asset-Manager. Die bisherigen E-Mail-Bilder (Logo, Header, Portraits) können aus der Web-App übernommen werden.",
      "importImages": "Standardbilder importieren",
      "imagesImported": "{count} Bilder importiert.",
      "errors": "Fehler",
      "showCode": "Code-Ansicht und MJML-Import im Editor anzeigen",
      "showCodeHint": "Für normale Benutzer ausgeschaltet lassen. Der HTML-Code wird immer auf dem Server erzeugt.",
      "conversionTitle": "Umwandlung erforderlich",
      "conversionText": "Diese Vorlage liegt noch im alten Format mit festen Textfeldern vor. Dieses Format wird nicht mehr unterstützt: Die Vorlage kann erst nach der Umwandlung bearbeitet, dupliziert oder versendet werden.",
      "conversionHint": "Bei der Umwandlung werden alle Inhalte (Texte, Button, Einschübe, Ansprechpartner) in den visuellen Editor übernommen. Rechts ist die bisherige Darstellung zum Vergleich.",
      "conversionPreview": "Bisherige Darstellung",
      "convert": "In visuelle Vorlage umwandeln",
      "converted": "Vorlage umgewandelt. Sie kann jetzt frei gestaltet werden.",
      "pleaseCompleteTheForm": "Bitte vervollständigen Sie Ihre Angaben.",
      "untitledCustomEmailTemplate": "Unbenannte E-Mail-Vorlage",
      "unsavedChangesTitle": "Ungespeicherte Änderungen",
      "unsavedChanges": "Die Änderungen an dieser Vorlage wurden noch nicht gespeichert. Seite trotzdem verlassen?",
      "stay": "Hier bleiben",
      "leave": "Verwerfen und verlassen",
      "actions": "Aktionen",
      "remove": "Löschen",
      "confirmDeleteTemplate": "Möchten Sie diese E-Mail-Vorlage endgültig löschen? Dies kann nicht rückgängig gemacht werden.",
      "cancel": "Abbrechen"
    },
    "en": {
      "back": "Back",
      "save": "Save",
      "saved": "Saved.",
      "design": "Design",
      "settings": "Settings",
      "previewAndTest": "Preview & test",
      "preview": "Preview",
      "textVersion": "Text version (for mail clients without HTML)",
      "sampleRecipient": "Sample recipient for placeholders (optional)",
      "testEmail": "Test email",
      "testEmailText": "Sends the last saved template with sample data to an address.",
      "testUsesSaved": "Note: unsaved changes are not included in the test email yet.",
      "recipient": "Recipient address",
      "sendTest": "Send test email",
      "testSent": "Test email handed over.",
      "developerOptions": "Developer options",
      "images": "Images",
      "importImagesHint": "Currently {count} images in the asset manager. The existing email images (logo, header, portraits) can be imported from the web app.",
      "importImages": "Import default images",
      "imagesImported": "{count} images imported.",
      "errors": "errors",
      "showCode": "Show code view and MJML import in the editor",
      "showCodeHint": "Keep off for regular users. The HTML is always generated on the server.",
      "conversionTitle": "Conversion required",
      "conversionText": "This template is still in the old format with fixed text fields. That format is no longer supported: the template can only be edited, duplicated or sent after conversion.",
      "conversionHint": "Conversion carries all content (texts, button, paragraphs, contact persons) over to the visual editor. The previous rendering is shown on the right for comparison.",
      "conversionPreview": "Previous rendering",
      "convert": "Convert to visual template",
      "converted": "Template converted. It can now be designed freely.",
      "pleaseCompleteTheForm": "Please complete the form.",
      "untitledCustomEmailTemplate": "Untitled email template",
      "unsavedChangesTitle": "Unsaved changes",
      "unsavedChanges": "Changes to this template have not been saved. Leave the page anyway?",
      "stay": "Stay",
      "leave": "Discard and leave",
      "actions": "Actions",
      "remove": "Delete",
      "confirmDeleteTemplate": "Do you really want to delete this email template? This cannot be undone.",
      "cancel": "Cancel"
    }
  }
</i18n>
